import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Search, ChevronDown, ChevronUp } from "lucide-react";

// ─── Types ──────────────────────────────────────────────────────────────────────

interface VariableItem {
  label: string;
  value: string; // e.g. {{contact.first_name}}
  key: string; // display key e.g. <contact_givenName>
}

interface VariableGroup {
  id: string;
  label: string;
  items: VariableItem[];
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
}

// ─── Build groups ────────────────────────────────────────────────────────────────

function buildGroups(attributes: any[]): VariableGroup[] {
  const contactGroup: VariableGroup = {
    id: "contact",
    label: "Variables from Contact fields",
    items: [
      { label: "Given Name", value: "{{contact.first_name}}", key: "<contact_givenName>" },
      { label: "Family Name", value: "{{contact.last_name}}", key: "<contact_familyName>" },
      { label: "Email", value: "{{contact.email}}", key: "<contact_email>" },
      { label: "Position/Title", value: "{{contact.title}}", key: "<contact_title>" },
      { label: "Phone", value: "{{contact.phone}}", key: "<contact_phoneNumbers>" },
      { label: "Address", value: "{{contact.address}}", key: "<contact_addresses>" },
    ],
  };

  const senderGroup: VariableGroup = {
    id: "sender",
    label: "Variables from Sender",
    items: [
      { label: "Sender Name", value: "{{sender.name}}", key: "<sender_name>" },
      { label: "Sender Email", value: "{{sender.email}}", key: "<sender_email>" },
    ],
  };

  const groups: VariableGroup[] = [contactGroup, senderGroup];

  if (attributes && attributes.length > 0) {
    const customItems: VariableItem[] = attributes
      .filter((attr: any) => {
        const id = typeof attr === "object" ? attr.id || attr.fieldId : attr;
        return id && !["name", "email", "phone"].includes(id);
      })
      .map((attr: any) => {
        const id = typeof attr === "object" ? attr.id || attr.fieldId : attr;
        const label = typeof attr === "object" ? attr.label || attr.name || id : id;
        return { label, value: `{{${id}}}`, key: `<${id}>` };
      });

    if (customItems.length > 0) {
      groups.push({ id: "custom", label: "Variables from Custom columns", items: customItems });
    }
  }

  return groups;
}

// ─── Component ───────────────────────────────────────────────────────────────────

const SubjectVariablePicker = ({ value, onChange, placeholder, disabled, errorMessage }: Props) => {
  const { t } = useTranslation();
  const attributes = useSelector((state: any) => state.template.get("attributes"));
  const attrJS = attributes ? attributes.toJS() : [];

  const groups = buildGroups(attrJS);

  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    contact: true,
    sender: false,
    custom: true,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowPicker(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // Auto-focus search when opened
  useEffect(() => {
    if (showPicker) setTimeout(() => searchRef.current?.focus(), 60);
  }, [showPicker]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "{") setTimeout(() => setShowPicker(true), 0);
    if (e.key === "Escape") {
      setShowPicker(false);
      setSearch("");
    }
  }, []);

  const handleInsertVariable = (varValue: string) => {
    if (!inputRef.current) return;
    const el = inputRef.current;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;

    // Remove trailing `{` that might have triggered the picker
    const before = value.slice(0, start).replace(/\{$/, "");
    const after = value.slice(end);
    const newValue = before + varValue + after;

    onChange(newValue);
    setShowPicker(false);
    setSearch("");

    requestAnimationFrame(() => {
      const pos = before.length + varValue.length;
      el.setSelectionRange(pos, pos);
      el.focus();
    });
  };

  const toggleGroup = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // Filter
  const filteredGroups = groups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (item) =>
          !search ||
          item.label.toLowerCase().includes(search.toLowerCase()) ||
          item.key.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="relative w-full">
      {/* Input + trigger button */}
      <div className="w-full h-9 flex items-center border border-gray-300 rounded-lg px-3 gap-1 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all bg-white">
        <input
          ref={inputRef}
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder || t("Enter subject... (type { to add variable)")}
          className="flex-1 outline-none text-sm placeholder:text-gray-400 bg-transparent"
        />
        {!disabled && (
          <button
            type="button"
            title={t("Insert variable")}
            onClick={() => setShowPicker((p) => !p)}
            className={`flex-shrink-0 inline-flex items-center gap-1 px-1.5 h-6 rounded text-xs font-medium transition-colors cursor-pointer
              ${
                showPicker
                  ? "bg-blue-50 text-blue-600"
                  : "text-gray-400 hover:text-blue-600 hover:bg-blue-50"
              }`}
          >
            <span className="font-mono">{"{ }"}</span>
          </button>
        )}
      </div>

      {errorMessage && <p className="text-xs text-red-600 mt-1">{errorMessage}</p>}

      {/* Dropdown — same design as EmailVariablePicker */}
      {showPicker && (
        <div
          ref={pickerRef}
          className="absolute top-full left-0 mt-1 bg-white rounded-2xl shadow-2xl z-50 overflow-hidden"
          style={{ width: 320, border: "1px solid #e5e7eb" }}
        >
          {/* Search */}
          <div className="px-4 pt-4 pb-0">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
              <Search size={16} className="text-gray-400 shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("Search variables")}
                className="flex-1 bg-transparent text-sm text-gray-700 outline-none placeholder-gray-400"
              />
            </div>
          </div>

          {/* Groups */}
          <div className="overflow-y-auto" style={{ maxHeight: 360 }}>
            {filteredGroups.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                {t("No variables found.")}
              </div>
            ) : (
              filteredGroups.map((group) => (
                <div key={group.id}>
                  {/* Group header */}
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center justify-between px-4 py-3 cursor-pointer select-none"
                  >
                    <span className="text-[11px] font-semibold tracking-widest text-gray-400 uppercase">
                      {group.label}
                    </span>
                    {expanded[group.id] ? (
                      <ChevronDown size={14} className="text-gray-400" />
                    ) : (
                      <ChevronUp size={14} className="text-gray-400" />
                    )}
                  </button>

                  {/* Items */}
                  {expanded[group.id] && (
                    <>
                      {group.items.map((item) => (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => handleInsertVariable(item.value)}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group"
                        >
                          <span className="text-base font-medium text-gray-800 group-hover:text-blue-700">
                            {item.label}
                          </span>
                          <span className="text-sm font-mono text-gray-400 group-hover:text-green-600 shrink-0 ml-4">
                            {item.key}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectVariablePicker;
